import { useCallback, useEffect, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { Btn } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { errorMessage, recordedLabel } from "../cockpit/format";
import { VERIFIED_TARGET, formatBytes, overVerifiedTarget, percent, refreshHeadline } from "./format";
import { isActive, startedBy } from "./refreshStatus";
import type { FacilityStats, FrsRefreshStatus, FrsRefreshView } from "./types";

interface Props {
  isAdmin: boolean;
  version: number;
  status: FrsRefreshStatus | null;
  onQueued: () => void;
}

const HISTORY_ROWS = 5;

export function RefreshPanel({ isAdmin, version, status, onQueued }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [stats, setStats] = useState<FacilityStats | null>(null);
  const [history, setHistory] = useState<FrsRefreshView[]>([]);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const statsResponse = await apiFetchRef.current("facilities/stats");
      setStats(await statsResponse.json());
      if (isAdmin) {
        const historyResponse = await apiFetchRef.current(`admin/frs/refreshes?limit=${HISTORY_ROWS}`);
        setHistory(await historyResponse.json());
      }
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [isAdmin]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const active = isActive(status);

  const refreshNow = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await apiFetchRef.current("admin/frs/refresh", { method: "POST", body: JSON.stringify({ force }) });
      onQueued();
    } catch (err) {
      setMessage(errorMessage(err));
      onQueued();
    } finally {
      setBusy(false);
    }
  };

  const last = stats?.lastRefresh ?? null;

  return (
    <Card className="mb-5">
      {error && <p className="text-rs-red text-[13px] m-0 mb-2">{error}</p>}
      <div className="flex flex-wrap items-start gap-x-10 gap-y-4">
        <div>
          <div className="text-[26px] font-semibold leading-none">{stats ? stats.total.toLocaleString() : "…"}</div>
          <div className="text-xs text-rs-light-gray mt-1">facilities in scope</div>
        </div>
        <div>
          <div className="text-[26px] font-semibold leading-none">
            {stats ? percent(stats.withCoordinates, stats.total) : "…"}
          </div>
          <div className="text-xs text-rs-light-gray mt-1">have coordinates</div>
        </div>
        <div>
          <div className="text-[26px] font-semibold leading-none">
            {stats ? (stats.total - stats.unmatched).toLocaleString() : "…"}
          </div>
          <div className="text-xs text-rs-light-gray mt-1">attached to a supplier</div>
        </div>
        <div>
          <div className="text-[26px] font-semibold leading-none">
            <span
              className={stats && overVerifiedTarget(stats.verified) ? "text-rs-amber" : "text-rs-green"}
              title={
                stats && overVerifiedTarget(stats.verified)
                  ? `Over the target of ${VERIFIED_TARGET.toLocaleString()}`
                  : undefined
              }
            >
              {stats ? stats.verified.toLocaleString() : "…"}
            </span>{" "}
            of {VERIFIED_TARGET.toLocaleString()}
          </div>
          <div className="text-xs text-rs-light-gray mt-1">ResinSmart Verified</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {stats &&
            Object.entries(stats.bySegment).map(([segment, count]) => (
              <Chip key={segment}>
                {segment} {count.toLocaleString()}
              </Chip>
            ))}
        </div>
        <div className="ml-auto text-[13px] text-rs-light-gray max-w-[420px]">
          {last ? (
            <>
              EPA's file of {recordedLabel(last.sourceLastModifiedUtc)} · {formatBytes(last.sourceSizeBytes)}
              <br />
              Loaded {recordedLabel(last.completedUtc ?? last.startedUtc)}: {refreshHeadline(last)}
            </>
          ) : (
            "No refresh has completed yet."
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="mt-4 pt-4 border-t border-rs-jet">
          <div className="flex flex-wrap items-center gap-4">
            <Btn variant="ghost" disabled={busy || active} onClick={refreshNow}>
              <span className={`material-symbols-sharp ${active ? "animate-spin" : ""}`}>sync</span>
              {status?.state === "Queued" ? "Refresh queued…" : active ? "Refresh running…" : "Refresh from EPA now"}
            </Btn>
            <label className="flex items-center gap-2 text-[13px] text-rs-light-gray">
              <input type="checkbox" checked={force} disabled={active} onChange={(e) => setForce(e.target.checked)} />
              Even if EPA's file has not changed
            </label>
            {status && active && startedBy(status) && (
              <span className="text-[13px] text-rs-light-gray">
                {startedBy(status)}. Only one refresh can run at a time.
              </span>
            )}
            {message && !active && <span className="text-[13px] text-rs-light-gray">{message}</span>}
          </div>
          {history.length > 0 && (
            <ul className="m-0 mt-3 p-0 list-none text-xs text-rs-light-gray flex flex-col gap-1">
              {history.map((r) => (
                <li key={r.refreshId}>
                  {recordedLabel(r.startedUtc)} · {r.triggeredBy === "Admin" ? "manual" : "scheduled"} ·{" "}
                  <Chip variant={r.status === "Succeeded" ? "ok" : r.status === "Failed" ? "bad" : "default"}>
                    {r.status}
                  </Chip>{" "}
                  {r.status === "Failed" ? (r.lastError ?? "") : refreshHeadline(r)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
